import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { formatNumber, loader } from 'helpers/generic';
import { getCommunity } from 'helpers/community';
import Table2Pdf from 'components/Table2Pdf';

const StyledReport = styled.div`
    padding-top: 20px;

    h1 {
        text-align: center;
        font-size: 25px;
        display: block;
        margin-bottom: 20px;
    }

    table {
        border-collapse: separate;
        border-spacing: 0;

        thead {
            position: sticky;
            top: 0;
            z-index: 10;
            background: white;

            tr {
                th {
                    text-transform: uppercase;
                    border: 1px solid #dee2e6;
                    border-bottom: 2px solid #00A7DF !important;
                    vertical-align: bottom;
                    font-size: 12px;
                    line-height: 12px;
                    text-align: center;
                    font-weight: normal;
                    clip-path: inset(0px 0px -8px 0px);
                    box-shadow: 0 4px 6px -2px rgba(0,0,0,0.12);

                    &#property {
                        width: 250px;
                        min-width: 250px;
                        position: sticky;
                        left: 0;
                        z-index: 11;
                        background: white;
                        border: 1px solid #dee2e6 !important;
                        clip-path: unset;
                        box-shadow: none;
                    }

                    &#coefficient {
                        width: 20px;
                    }

                    &#initial_balance {
                        width: 50px;
                    }

                    &#expenses {
                        width: 80px;
                    }

                    &#paid {
                        width: 80px;
                    }

                    &#refunded {
                        width: 80px;
                    }

                    &#final_balance {
                        width: 50px;
                    }

                    &.estimate_expense {
                        position: relative;
                        padding-top: 25px;

                        span {
                            position: absolute;
                            top: 0;
                            left: 0;
                            display: block;
                            width: 100%;
                            font-size: 10px;
                            font-weight: normal;
                            margin-top: 3px;
                        }

                        small {
                            display: block;
                            font-size: 9px;
                            line-height: 9px;
                            color: gray;
                            margin-top: 5px;
                        }

                        b {
                            display: block;
                            margin-top: 5px;
                            font-weight: 600;
                            font-size: 10px;
                            line-height: 10px;
                        }
                    }
                }
            }
        }

        tbody {
            tr {
                td {
                    text-align: center;
                    border: 1px solid #dee2e6;

                    &:nth-child(1) {
                        text-align: left;
                        position: sticky;
                        left: 0;
                        background: white;
                        z-index: 1;
                        border-right: 2px solid #00A7DF;
                        clip-path: inset(0px -8px 0px 0px);
                        box-shadow: 4px 0 6px -2px rgba(0,0,0,0.12);
                    }
                }
            }
        }

        tfoot {
            tr {
                td {
                    text-align: center;
                    font-weight: bold;
                    border: 1px solid #dee2e6;

                    &:nth-child(1) {
                        position: sticky;
                        left: 0;
                        background: white;
                        z-index: 1;
                        border-right: 2px solid #00A7DF;
                        clip-path: inset(0px -8px 0px 0px);
                        box-shadow: 4px 0 6px -2px rgba(0,0,0,0.12);
                    }
                }
            }
        }

        &.exporting {
            thead {
                position: static !important;
                tr th {
                    clip-path: none !important;
                    box-shadow: none !important;
                    &:not(.estimate_expense) {
                        position: static !important;
                    }
                    &#property {
                        position: static !important;
                    }
                }
            }
            tbody tr td:first-child,
            tfoot tr td:first-child {
                position: static !important;
                clip-path: none !important;
                box-shadow: none !important;
            }
        }
    }

`;

let pageTitleEl = null;
let pageContentEl = null;
let layoutEl = null;
let cardBalanceEl = null;
let cardBalanceBodyEl = null;

let layoutElOverflowOriginal = null;
let pageTitleElPositionOriginal = null;
let pageContentElPaddingOriginal = null;
let pageContentElOverflowOriginal = null;
let cardBalanceElHeightOriginal = null;
let cardBalanceBodyElOverflowOriginal = null;

let axiosCancelToken = null;

const ExpensesDistribution = (props) => {
    const year = props.year;
    const setExportBtn = props.setExportBtn;
    const tableRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [cacheInfo, setCacheInfo] = useState(null);
    const [jobInfo, setJobInfo] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    // Estado para detectar si hay una nueva versión del balance
    const [hasNewVersion, setHasNewVersion] = useState(false);
    const lastGeneratedAtRef = useRef(null); // Usamos ref persistente

    // Modificado: fetchCache acepta un flag para no mostrar loading (por ejemplo, en polling)
    const fetchCache = useCallback(async (showLoading = true) => {
        if (showLoading) setLoading(true);
        setError(null);
        setData(null);
        setCacheInfo(null);
        setJobInfo(null);
        try {
            const res = await axios.get('/api/manager/balances/cached', {
                params: {
                    community_id: getCommunity()?.id,
                    type: 'expensesDistribution',
                    financial_year: year
                }
            });
            setCacheInfo(res.data.cache);
            setJobInfo(res.data.job);
            setData(res.data.cache?.data || null);
            // Actualizamos la ref con el generated_at actual
            lastGeneratedAtRef.current = res.data.cache?.generated_at || null;
        } catch (e) {
            setError('No se pudo obtener el balance.');
        }
        if (showLoading) setLoading(false);
    }, [year]);

    useEffect(() => {
        fetchCache();
    }, [year, fetchCache]);

    const handleRequestJob = async () => {
        setRefreshing(true);
        setError(null);
        try {
            await axios.post('/api/manager/balances/request-job', {
                community_id: getCommunity()?.id,
                type: 'expensesDistribution',
                financial_year: year
            });
            fetchCache();
        } catch (e) {
            if (e.response && e.response.status === 409) {
                setError('Ya hay un balance en proceso para este año.');
            } else {
                setError('Error al solicitar la generación del balance.');
            }
        }
        setRefreshing(false);
    };

    // Fix scrolls
    useEffect(() => {
        pageTitleEl = document.querySelector('.page-title');
        pageContentEl = document.querySelector('.page-content');
        layoutEl = document.querySelector('#layout');
        cardBalanceEl = document.querySelector('#card-balance');
        cardBalanceBodyEl = cardBalanceEl.querySelector('.card-body');
        
        const scrollFix = (e) => {
            let windowHeight = window.innerHeight;

            // Remove layout overflow scroll
            if ( !layoutElOverflowOriginal ) layoutElOverflowOriginal = layoutEl.style.overflow;
            layoutEl.style.overflow = 'hidden';

            // Remove title sticky
            if ( !pageTitleElPositionOriginal ) pageTitleElPositionOriginal = pageTitleEl.style.position;
            pageTitleEl.style.position = 'unset';

            // Remove padding
            if ( !pageContentElPaddingOriginal ) pageContentElPaddingOriginal = pageContentEl.style.padding;
            pageContentEl.style.padding = 0;
            if ( !pageContentElOverflowOriginal ) pageContentElOverflowOriginal = pageContentEl.style.overflow;
            pageContentEl.style.overflow = 'hidden';

            // Calc height
            let pageTitleBounding = pageTitleEl.getBoundingClientRect();
            let height = windowHeight - pageTitleBounding.top - pageTitleBounding.height;
            pageContentEl.style.height = height + 'px';
            // console.log(height)

            // Set overflow to card balance
            if ( !cardBalanceElHeightOriginal ) cardBalanceElHeightOriginal = cardBalanceEl.style.height;
            cardBalanceEl.style.height = '100%';
            if ( !cardBalanceBodyElOverflowOriginal ) cardBalanceBodyElOverflowOriginal = cardBalanceBodyEl.style.overflow;
            cardBalanceBodyEl.style.overflow = 'scroll';
        }
        window.addEventListener('resize', scrollFix);
        scrollFix();
        
        return () => {
            window.removeEventListener('resize', scrollFix);

            // Reset all changes
            layoutEl.style.overflow = layoutElOverflowOriginal;
            pageTitleEl.style.position = pageTitleElPositionOriginal;
            pageContentEl.style.padding = pageContentElPaddingOriginal;
            pageContentEl.style.overflow = pageContentElOverflowOriginal;
            cardBalanceEl.style.height = cardBalanceElHeightOriginal;
            cardBalanceBodyEl.style.overflow = cardBalanceBodyElOverflowOriginal;
        }
    }, []);

    // Controla si ya se hizo el fetch inicial para evitar doble request-job
    const [fetched, setFetched] = useState(false);
    const pollingRef = useRef(false);
    const lastJobStatus = useRef(null);

    // Fetch inicial sólo una vez (ignora StrictMode doble render)
    useEffect(() => {
        let ignore = false;
        if (!fetched && !ignore) {
            setFetched(true);
            fetchCache();
        }
        return () => { ignore = true; };
    }, [fetched, fetchCache]);

    // Polling cada 5s si hay un job en proceso, pero sólo para detectar nueva versión
    useEffect(() => {
        let interval = null;
        if (jobInfo && (jobInfo.status === 'pending' || jobInfo.status === 'processing')) {
            pollingRef.current = true;
            // Guardamos el generated_at actual en la ref al iniciar polling
            lastGeneratedAtRef.current = cacheInfo?.generated_at || null;
            interval = setInterval(async () => {
                try {
                    const res = await axios.get('/api/manager/balances/cached', {
                        params: {
                            community_id: getCommunity()?.id,
                            type: 'expensesDistribution',
                            financial_year: year
                        }
                    });
                    const newGeneratedAt = res.data.cache?.generated_at || null;
                    // Si el job ya NO está en proceso, paramos el polling y actualizamos jobInfo
                    if (res.data.job && (res.data.job.status !== 'pending' && res.data.job.status !== 'processing')) {
                        setJobInfo(res.data.job);
                        // Si hay nueva versión, mostramos el mensaje
                        if (
                            (lastGeneratedAtRef.current && newGeneratedAt && newGeneratedAt !== lastGeneratedAtRef.current) ||
                            (!lastGeneratedAtRef.current && newGeneratedAt) // <-- caso: antes no había cache, ahora sí
                        ) {
                            setHasNewVersion(true);
                        }
                        pollingRef.current = false;
                        clearInterval(interval);
                    } else if (
                        (lastGeneratedAtRef.current && newGeneratedAt && newGeneratedAt !== lastGeneratedAtRef.current) ||
                        (!lastGeneratedAtRef.current && newGeneratedAt)
                    ) {
                        setHasNewVersion(true);
                    }
                } catch {}
            }, 5000);
        } else {
            pollingRef.current = false;
        }
        return () => {
            if (interval) clearInterval(interval);
            pollingRef.current = false;
        };
    }, [jobInfo, cacheInfo, year]);

    // Handler para recargar los datos manualmente
    const handleReload = async () => {
        setHasNewVersion(false);
        await fetchCache();
    }

    // Si hay un job en proceso, ocultar el aviso de nueva versión
    useEffect(() => {
        if (jobInfo && (jobInfo.status === 'pending' || jobInfo.status === 'processing')) {
            setHasNewVersion(false);
        }
    }, [jobInfo]);

    // useEffect para mostrar/ocultar botón de exportar
    useEffect(() => {
        if (data !== null && !loading) {
            setExportBtn(<ExportBtn tableRef={tableRef} year={year} />);
        } else {
            setExportBtn(null);
        }
    }, [year, setExportBtn, data, loading]);

    let totals = {
        coefficient: 0,
        initial_balance: 0,
        groups_expenses: {},
        total_expenses: 0,
        total_paid: 0,
        total_refunded: 0,
        final_balance: 0
    };

	return (
        <>
            {loading && <div style={{textAlign: 'center'}}>{loader}</div>}

            {error ? (
                <div className="alert alert-danger m-3" role="alert">{error}</div>
            ) :
            (!hasNewVersion && !cacheInfo && !loading && !(jobInfo && (jobInfo.status === 'pending' || jobInfo.status === 'processing'))) && (
                <div className="alert alert-warning m-3" role="alert">No hay balance en cache para este año.</div>
            )}

            {cacheInfo && cacheInfo.generated_at && (
                <div className="alert alert-info m-3" role="alert">
                    Balance generado el: {new Date(cacheInfo.generated_at).toLocaleString()}
                </div>
            )}

            {/* Mostrar aviso de nueva versión solo si hay una nueva versión Y no hay job en proceso */}
            {hasNewVersion && (!jobInfo || (jobInfo.status !== 'pending' && jobInfo.status !== 'processing')) && (
                <div className="alert alert-success m-3 d-flex align-items-center justify-content-between" role="alert">
                    <span>Hay una nueva versión del balance disponible.</span>
                    <button className="btn btn-sm btn-success text-white ms-3" onClick={handleReload}>Recargar datos</button>
                </div>
            )}

            {/* Mostrar botón de generar solo si NO hay job en proceso y NO hay nueva versión pendiente de recarga */}
            {!hasNewVersion && (!jobInfo || (jobInfo.status !== 'pending' && jobInfo.status !== 'processing')) && (
                <button className="btn btn-primary text-white m-3 my-0" onClick={handleRequestJob} disabled={refreshing}>
                    {refreshing ? 'Solicitando...' : 'Generar/Recalcular balance'}
                </button>
            )}

            {/* Mostrar aviso de proceso solo si NO hay nueva versión pendiente de recarga y hay job en proceso */}
            {!hasNewVersion && jobInfo && (jobInfo.status === 'pending' || jobInfo.status === 'processing') && (
                <div className="alert alert-warning m-3" role="alert">Hay un balance en proceso para este año. Por favor, espera a que termine.</div>
            )}

            {(data !== null && !loading) && 
                <StyledReport>
                    <h1 className="title">DISTRIBUCIÓN DE GASTOS {year}</h1>
                    <table className="table table-bordered" ref={tableRef}>
                        <thead>
                            <tr>
                                <th id="property">PROPIEDAD</th>
                                <th id="coefficient">COEF.</th>
                                <th id="initial_balance">SALDO INICIAL</th>
                                {data?.groups_expenses?.map((el, idx) => {
                                    return (
                                        <th className="estimate_expense" key={idx}>
                                            <span>{el.estimate_name}</span>
                                            <small>{el.title_name}</small>
                                            <b>{el.denomination}</b>
                                        </th>
                                    )
                                })}
                                {data?.groups_expenses?.length === 0 &&
                                    <th>No hay distribución de gastos</th>
                                }
                                <th id="expenses">TOTAL GASTOS</th>
                                <th id="paid">GASTO PAGADO</th>
                                {/* <th id="refunded">DEVUELTO</th> */}
                                <th id="final_balance">SALDO FINAL</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data?.properties?.map((el, idx) => {

                                totals.coefficient += parseFloat(el.coefficient);
                                totals.initial_balance += parseFloat(data?.initial_balances[el.id] ?? 0);
                                totals.total_expenses += data?.total_expenses_by_properties[el.id] ?? 0;
                                totals.total_paid += data?.total_paid_by_properties[el.id] ?? 0;
                                totals.total_refunded += data?.total_refunded_by_properties[el.id] ?? 0;
                                totals.final_balance += data?.final_balances[el.id] ?? 0;

                                return (
                                    <tr key={idx}>
                                        <td>{el.name}</td>
                                        <td>{formatNumber(el.coefficient, 2)}</td>
                                        <td>{formatNumber(data?.initial_balances[el.id] ?? 0, 2)}</td>
                                        {data?.groups_expenses?.map((gEl, gIdx) => {
                                            if ( !totals.groups_expenses['ge'+gEl.id] ) totals.groups_expenses['ge'+gEl.id] = 0;
                                            totals.groups_expenses['ge'+gEl.id] += parseFloat(data?.expenses_by_properties[el.id][gEl.id] ?? 0);

                                            return (
                                                <td className="estimate_expense" key={gIdx}>{formatNumber(data?.expenses_by_properties[el.id][gEl.id] ?? 0, 2)}</td>
                                            )
                                        })}
                                        {data?.groups_expenses?.length === 0 &&
                                        <td></td>
                                        }
                                        <td>{formatNumber(data?.total_expenses_by_properties[el.id] ?? 0, 2)}</td>
                                        <td>{formatNumber((data?.total_paid_by_properties[el.id] ?? 0) + (data?.total_refunded_by_properties[el.id] ?? 0), 2)}</td>
                                        {/* <td>{formatNumber(data?.total_paid_by_properties[el.id] ?? 0, 2)}</td> */}
                                        {/* <td>{formatNumber(data?.total_refunded_by_properties[el.id] ?? 0, 2)}</td> */}
                                        <td>{formatNumber(data?.final_balances[el.id] ?? 0, 2)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td>TOTAL</td>
                                <td>{formatNumber(totals.coefficient, 2)}</td>
                                <td>{formatNumber(totals.initial_balance, 2)}</td>
                                {totals.groups_expenses && Object.keys(totals.groups_expenses).map((el, idx) => {
                                    return (
                                        <td key={idx}>{formatNumber(totals.groups_expenses[el], 2)}</td>
                                    );
                                })}
                                <td>{formatNumber(totals.total_expenses, 2)}</td>
                                <td>{formatNumber(totals.total_paid, 2)}</td>
                                {/* <td>{formatNumber(totals.total_refunded, 2)}</td> */}
                                <td>{formatNumber(totals.final_balance, 2)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </StyledReport>
            }
        </>
	);
}

export default ExpensesDistribution;

const ExportBtn = (props) => {
    const {tableRef, year} = props;

    const pdfExporterRef = useRef(null);
    
    const exportExcel = async () => {
        axiosCancelToken = axios.CancelToken.source();

        await axios.post('api/manager/balances/export/expenses-distribution', {
            community_id: getCommunity()?.id,
            financial_year: year
        }, {
            responseType: 'blob',
            cancelToken: axiosCancelToken.token
        }).then((response) => {
            let fileName = response.headers.file_name;
    
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.parentElement.removeChild(link);
        }).catch((error) => {
            if ( axios.isCancel(error) ) return;
        });

        console.log('sasas')
    }

    const exportPdf = () => {
        pdfExporterRef.current.export();
    }

    const removeStickyStyles = () => {
        if (tableRef.current) tableRef.current.classList.add('exporting');
    };

    const restoreStickyStyles = () => {
        if (tableRef.current) tableRef.current.classList.remove('exporting');
    };

    return (
        <div className="dropdown ms-2">
            <button
                className="btn btn-sm btn-light dropdown-toggle"
                type="button"
                id="exportDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false">
                Exportar&nbsp;
            </button>
            <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                <li>
                    <button className="dropdown-item" onClick={() => exportExcel()}>
                        Exportar Excel
                    </button>
                </li>
                <li>
                    <button className="dropdown-item" onClick={() => exportPdf()}>
                        Exportar PDF
                    </button>
                    <Table2Pdf
                        ref={pdfExporterRef}
                        tableRef={tableRef}
                        button={<></>}
                        pageOrientation="landscape"
                        pdfName={'distribución-gastos-' + year}
                        onBeforeExport={removeStickyStyles}
                        onAfterExport={restoreStickyStyles}
                    />
                </li>
            </ul>
        </div>
    );
}